import React, { useState } from 'react'
import _ from 'lodash'
import moment from 'moment'
import EditCell from './EditCell'
import Tooltip from './Tooltip'
import TableHeaderSortable from './TableHeaderSortable'
import EditCellDropdown from './EditCellDropdown'
import SentStatusModal from './SentStatusModal'
import {
  updateLeadField,
  updateLeadNote,
  deleteLead,
  assignLeadToNewClient,
} from '../fire'
import {
  rowsPerPage,
  statusList,
  statusColors,
  unassignedClientId,
} from '../constants'
import { formatPhone, formatNumber } from '../utils'
import api from '../api'
import EditCellCheckbox from './EditCellCheckbox'

let parseNumber = str => {
  let num = parseFloat(formatNumber(str))
  return _.isFinite(num) ? num : null
}

let Edit = ({ lead, month, field, fmt, ...props }) => {
  return (
    <EditCell
      value={lead[field]}
      onUpdate={v =>
        updateLeadField({
          month,
          field,
          clientId: lead.clientId,
          leadId: lead.leadId,
          value: fmt ? fmt(v) : v,
        })
      }
      {...props}
    />
  )
}

let MasterTable = ({
  clients,
  page,
  leads,
  setLeads,
  leadNotes,
  sort,
  setSort,
  month,
  partnerClientIds = [],
  adminPermissions: { fullAccess, deleteLeadAccess, partnerAccess } = {},
  editClientIdAllowed = false,
}) => {
  let [sendLeadId, setSendLeadId] = useState('')
  let totalAmount = _.sumBy(leads, l => l.amount || 0)
  let closedAmount = _.filter(leads, { closed: true }).length
  let THSort = ({ children, field, ...props }) => {
    return (
      <TableHeaderSortable
        sort={sort}
        setSort={setSort}
        field={field}
        {...props}
      >
        {children}{' '}
      </TableHeaderSortable>
    )
  }

  let canDeleteLead = fullAccess || deleteLeadAccess
  return (
    <table className="table table-striped table-bordered table-sm small">
      <thead>
        <tr>
          <th />
          <THSort field="clientId">Client ID</THSort>
          <THSort field="name">Name</THSort>
          <THSort field="phoneNumber">Phone Number</THSort>
          <THSort field="email">Email</THSort>
          <th>Address</th>
          <THSort field="date">Date/Time</THSort>
          <THSort field="city">City</THSort>
          <THSort field="status" style={{ minWidth: '110px' }}>
            Status
          </THSort>
          <THSort field="closed">
            Closed
            {closedAmount ? <>: {closedAmount.toLocaleString('en-us')}</> : ''}
          </THSort>
          <THSort field="amount">
            Amount
            {totalAmount ? <>: ${totalAmount.toLocaleString('en-us')}</> : ''}
          </THSort>
          <th>Notes</th>
          {canDeleteLead && <th />}
        </tr>
      </thead>
      <tbody>
        {_(leads)
          .orderBy([l => _.toLower(l[sort.field])], [sort.order])
          .value()
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map(l => {
            let leadClientId = l.clientId || l.leadClientId
            let lead = { ...l, clientId: leadClientId }
            let leadNote = _.get(leadNotes, [leadClientId, l.leadId], '')
            let client = _.find(clients, { id: leadClientId }) || {}
            let cityDropdownOptions = !client.cityDropdownOptions
              ? []
              : client.cityDropdownOptions.split('\n')
            return (
              <tr key={leadClientId + lead.leadId}>
                <td className="p-1 m-0 align-middle">
                  <div
                    className="rounded-circle ml-1"
                    style={{
                      background: statusColors[lead.status],
                      width: '8px',
                      height: '8px',
                    }}
                  />
                </td>
                <td>
                  {editClientIdAllowed ? (
                    <Tooltip title={lead.parseMapping || 'No parse mapping'}>
                      {partnerAccess ? (
                        <EditCellDropdown
                          align="left"
                          options={partnerClientIds}
                          value={leadClientId}
                          warnForMissingValues={false}
                          onChange={async ({ value, setDropdownValue }) => {
                            if (
                              leadClientId !== unassignedClientId &&
                              !window.confirm(
                                'Are you sure you want re-assign the Client ID?'
                              )
                            ) {
                              return
                            }
                            setDropdownValue(value)
                            await assignLeadToNewClient({
                              month,
                              lead: l,
                              newClientId: value,
                              leadNote,
                            })
                            setLeads(prev =>
                              prev.filter(v => v.leadId !== lead.leadId)
                            )
                          }}
                        />
                      ) : (
                        <EditCell
                          value={leadClientId}
                          warningMsg={
                            leadClientId !== unassignedClientId &&
                            'Are you sure you want edit the Client ID?  This will re-assign the lead to the new Client ID'
                          }
                          emptyAllowed={false}
                          onUpdate={v =>
                            assignLeadToNewClient({
                              month,
                              lead,
                              newClientId: v,
                              leadNote,
                            })
                          }
                        />
                      )}
                    </Tooltip>
                  ) : (
                    leadClientId
                  )}
                </td>
                <td>
                  <Edit lead={lead} month={month} field="name" />
                </td>
                <td>
                  <Edit
                    lead={lead}
                    month={month}
                    field="phoneNumber"
                    fmt={formatPhone}
                  />
                </td>
                <td>
                  <Edit lead={lead} month={month} field="email" />
                </td>
                <td>
                  <Edit lead={lead} month={month} field="address" />
                </td>
                <td className="text-nowrap">
                  {lead.sentDate ? (
                    <div>
                      {moment(lead.sentDate).format('MM/DD/YYYY h:mmA')}{' '}
                      <Tooltip
                        title={`Received ${moment(lead.date).format(
                          'MM/DD/YYYY h:mmA'
                        )}`}
                        style={{ display: 'inline-block' }}
                        align="right"
                      >
                        <i className="fa fa-info-circle" />
                      </Tooltip>
                    </div>
                  ) : (
                    moment(lead.date).format('MM/DD/YYYY h:mmA')
                  )}
                </td>
                <td>
                  <EditCellDropdown
                    allowCustomValue
                    warnForMissingValues={false}
                    options={cityDropdownOptions}
                    value={lead.city}
                    emptyOptionsText="No cities available for this client.  Add them in the client's settings"
                    onChange={async ({ value, setDropdownValue }) => {
                      setDropdownValue(value)
                      updateLeadField({
                        month,
                        field: 'city',
                        clientId: lead.clientId,
                        leadId: lead.leadId,
                        value,
                      })
                    }}
                  />
                </td>
                <td>
                  {sendLeadId === lead.leadId && (
                    <SentStatusModal
                      onClose={() => setSendLeadId('')}
                      client={client}
                      month={month}
                      lead={lead}
                      leadNote={leadNote}
                    />
                  )}
                  <EditCellDropdown
                    disabled={lead.status === 'invoiced'}
                    options={statusList.filter(s => s !== 'invoiced')}
                    value={lead.status}
                    selectedValueFormat={v =>
                      `${_.startCase(v)}${
                        lead.billingType !== 'perLead' ? '*' : ''
                      }`
                    }
                    optionFormat={_.startCase}
                    onChange={async ({ value, setDropdownValue }) => {
                      if (value === 'sent') {
                        setSendLeadId(lead.leadId)
                        return
                      } else if (value === 'duplicate') {
                        setDropdownValue(value)
                        await updateLeadField({
                          month,
                          field: 'status',
                          clientId: lead.clientId,
                          leadId: lead.leadId,
                          value,
                        })

                        await api('/trigger-duplicate-status-email', {
                          clientId: lead.clientId,
                          name: lead.name,
                          phoneNumber: lead.phoneNumber,
                          email: lead.email,
                          address: lead.address,
                          city: lead.city,
                          notes: leadNote,
                        })
                      } else {
                        setDropdownValue(value)
                        updateLeadField({
                          month,
                          field: 'status',
                          clientId: lead.clientId,
                          leadId: lead.leadId,
                          value,
                        })
                      }
                    }}
                  />
                </td>
                <td className="text-center">
                  <EditCellCheckbox
                    checked={lead.closed}
                    onUpdate={checked =>
                      updateLeadField({
                        month,
                        field: 'closed',
                        clientId: lead.clientId,
                        leadId: lead.leadId,
                        value: checked || null, // null instead of false delete field
                      })
                    }
                  />
                </td>
                <td className="text-center">
                  <Edit
                    lead={lead}
                    month={month}
                    field="amount"
                    fmt={parseNumber}
                  />
                </td>
                <td>
                  <Tooltip title={leadNote} align="right" width="200px" wrap>
                    <EditCell
                      value={leadNote}
                      onUpdate={v =>
                        updateLeadNote({
                          month,
                          clientId: lead.clientId,
                          leadId: lead.leadId,
                          value: v,
                        })
                      }
                    />
                  </Tooltip>
                </td>
                {canDeleteLead && (
                  <td>
                    <button
                      className="btn btn-danger btn-sm"
                      style={{ lineHeight: 1 }}
                      onClick={() => {
                        if (
                          window.confirm(
                            'Are you sure you want to delete the lead?'
                          )
                        ) {
                          deleteLead({
                            month,
                            clientId: lead.clientId,
                            leadId: lead.leadId,
                          })
                        }
                      }}
                    >
                      <i className="fa fa-remove" />
                    </button>
                  </td>
                )}
              </tr>
            )
          })}
      </tbody>
    </table>
  )
}

export default MasterTable
